import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    X, CaretDown, Sparkle, Crown, Camera, MapTrifold,
    Wrench, PaperPlaneTilt, ShareFat, Lifebuoy, Shield, ChatCircleDots,
} from '@phosphor-icons/react';
import { HELP_SECTIONS, pickDefaultSlug } from '../lib/helpContent';
import { useEntitlements } from '../context/EntitlementsContext';
import { restartOnboardingTour } from './OnboardingTour';

const ICONS = {
    Sparkle, Crown, Camera, MapTrifold, Wrench, PaperPlaneTilt, ShareFat, Lifebuoy, Shield, ChatCircleDots,
};

/** Slide-in help drawer anchored to the right edge. Auto-expands the
 *  section most relevant to the user's current page + tier. */
export default function HelpDrawer({ open, onClose }) {
    const location = useLocation();
    const ent = useEntitlements();
    const tier = ent?.tier || 'foundation';
    const initial = useMemo(
        () => pickDefaultSlug(location?.pathname, tier),
        [location?.pathname, tier],
    );
    const [openSlug, setOpenSlug] = useState(initial);

    // Reset to the contextually-chosen section every time the drawer is reopened
    useEffect(() => { if (open) setOpenSlug(initial); }, [open, initial]);

    // Esc to close
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[55] flex justify-end no-print"
            data-testid="help-drawer"
        >
            {/* Backdrop */}
            <button
                type="button"
                aria-label="Close help"
                onClick={onClose}
                className="absolute inset-0 bg-black/50 backdrop-blur-[2px] cursor-default"
            />

            {/* Panel */}
            <aside
                className="relative w-full max-w-md bg-white border-l-4 border-[#1FA5CF] flex flex-col shadow-2xl animate-in slide-in-from-right duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between px-5 py-4 border-b border-[#18181B] bg-[#18181B] text-white">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">DefectR</div>
                        <h2 className="font-display font-bold text-lg leading-tight">Help & how-to</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="close-help-drawer"
                        aria-label="Close help"
                        className="p-2 -mr-2 hover:bg-white/10 active:scale-95 transition-all duration-150"
                    >
                        <X size={20} weight="bold" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto">
                    {HELP_SECTIONS.map(sec => {
                        const Icon = ICONS[sec.icon] || Sparkle;
                        const isOpen = openSlug === sec.slug;
                        return (
                            <div key={sec.slug} className="border-b border-[#E4E4E7]">
                                <button
                                    type="button"
                                    onClick={() => setOpenSlug(isOpen ? null : sec.slug)}
                                    data-testid={`help-section-${sec.slug}`}
                                    aria-expanded={isOpen}
                                    className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${isOpen ? 'bg-[#E8F5FB]' : 'hover:bg-[#FAFAFA]'}`}
                                >
                                    <Icon size={20} weight="bold" color={isOpen ? '#1787AB' : '#52525B'} />
                                    <span className={`flex-1 font-display font-semibold ${isOpen ? 'text-[#1787AB]' : 'text-[#09090B]'}`}>
                                        {sec.title}
                                    </span>
                                    <CaretDown
                                        size={14}
                                        weight="bold"
                                        className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                                        color="#52525B"
                                    />
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 pt-1 text-sm text-[#09090B] leading-relaxed space-y-3">
                                        {sec.body.map((blk, i) => <Block key={i} blk={blk} />)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div className="px-5 py-6 text-center border-t border-[#E4E4E7] bg-[#FAFAFA]">
                        <button
                            type="button"
                            onClick={() => { restartOnboardingTour(); onClose?.(); }}
                            data-testid="help-replay-tour"
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#1787AB] hover:text-[#09090B] underline"
                        >
                            <Sparkle size={12} weight="fill" /> Replay the welcome tour
                        </button>
                        <p className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-[#52525B]">
                            Still stuck?
                        </p>
                        <a
                            href="mailto:support@defectr.co.uk"
                            className="mt-2 inline-block font-display font-semibold text-[#1787AB] underline"
                            data-testid="help-support-mailto"
                        >
                            support@defectr.co.uk
                        </a>
                    </div>
                </div>
            </aside>
        </div>
    );
}

function Block({ blk }) {
    if (blk.type === 'p') return <p>{blk.text}</p>;
    if (blk.type === 'ul') return (
        <ul className="list-disc pl-5 space-y-1.5">
            {blk.items.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
    );
    if (blk.type === 'ol') return (
        <ol className="list-decimal pl-5 space-y-1.5">
            {blk.items.map((t, i) => <li key={i}>{t}</li>)}
        </ol>
    );
    if (blk.type === 'note') return (
        <div className="border-l-4 border-[#1FA5CF] bg-[#E8F5FB] px-3 py-2 text-xs text-[#1787AB] italic">
            {blk.text}
        </div>
    );
    if (blk.type === 'table') return (
        <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-xs border border-[#E4E4E7]">
                <thead className="bg-[#FAFAFA]">
                    <tr>{blk.headers.map((h, i) => (
                        <th key={i} className="px-2 py-1.5 text-left font-mono uppercase tracking-wider text-[10px] text-[#52525B] border-b border-[#E4E4E7]">{h}</th>
                    ))}</tr>
                </thead>
                <tbody>
                    {blk.rows.map((r, ri) => (
                        <tr key={ri} className="border-b border-[#E4E4E7] last:border-b-0">
                            {r.map((c, ci) => (
                                <td key={ci} className="px-2 py-1.5 align-top">{c}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
    return null;
}
